import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  clearProperties,
  downloadShapefile,
  getPropertyCounts,
  importProperties,
  listHierarchyImports,
  undoHierarchyImport,
} from "@/lib/api";
import type { HierarchyDeleteSummary } from "@/lib/api";
import type {
  HierarchyImportRecord,
  PropertyCounts,
  PropertyImportResult,
} from "@/lib/types";
import { Banner, Button, ConfirmDialog, EmptyState, Panel, Spinner } from "./ui";
import { useLanguage } from "@/i18n";
import { formatDate } from "@/lib/format";

/** Import records for property outlines carry this level -- see the backend's
 *  routers/property_import.py. They share the hierarchy_imports table with
 *  zone/ward/block uploads, so both panels filter the same listing. */
const FEATURE_LEVEL = "feature";

/**
 * Uploading the property outlines the office already holds, so surveyors number
 * them instead of tracing them.
 *
 * This replaces the reference-layer panel, and the difference is worth stating
 * because the two look similar from here. A reference layer was read-only
 * context drawn under the map: a surveyor could see the municipal footprint but
 * still had to trace their own outline over every property by hand. What is
 * uploaded here becomes the survey rows themselves, so the only step left in the
 * field is the house number.
 *
 * **Uploading again is how the data is kept up to date.** A later file adds the
 * outlines that are new and skips the ones already present, matched on geometry
 * rather than on any id the source file happens to carry -- so a municipal
 * export re-issued with another month's construction can simply be dropped in.
 *
 * Two ways out, deliberately different in weight:
 *
 * * **Undo**, offered only for an upload made in this page session. It is for
 *   the mistake you just made -- wrong file, wrong project -- and it disappears
 *   on refresh precisely so it cannot become a casual way to delete survey work
 *   weeks later.
 * * **Delete all outlines**, which is permanent, asks twice, and will not
 *   proceed until the field data has been exported.
 */
export default function PropertyImportPanel({
  projectId,
  /** Bumped by the parent when the hierarchy changes; outlines are matched to
   *  blocks at import time, so the counts move when blocks do. */
  refreshKey = 0,
  onImported,
}: {
  projectId: number;
  refreshKey?: number;
  onImported: () => void;
}) {
  const { t, lang } = useLanguage();
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PropertyImportResult | null>(null);
  const [counts, setCounts] = useState<PropertyCounts | null>(null);
  const [imports, setImports] = useState<HierarchyImportRecord[] | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  /**
   * Uploads made since this page loaded -- the only ones that offer an undo.
   *
   * Held in component state rather than read back from the server, which is
   * what makes it vanish on refresh. Undo exists to reverse the file you just
   * dropped by mistake; leaving it on every historical upload forever turned a
   * one-click button into a way to delete months of collected numbering, which
   * is what "Delete all outlines" is for -- and that asks twice and demands an
   * export first.
   */
  const [sessionImportIds, setSessionImportIds] = useState<number[]>([]);

  // Undo: one confirmation, since it only ever reaches an upload from this
  // session. The blocked stage still exists because even a fresh upload can
  // have been numbered already by a surveyor working at the same time.
  const [undoFlow, setUndoFlow] = useState<{
    record: HierarchyImportRecord;
    stage: "confirm" | "blocked";
    summary?: HierarchyDeleteSummary;
  } | null>(null);

  // Delete-everything: two confirmations, always, plus the export gate.
  const [clearStage, setClearStage] = useState<"first" | "final" | null>(null);

  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false);

  const reload = useCallback(async () => {
    getPropertyCounts(projectId)
      .then(setCounts)
      .catch(() => {
        /* Non-fatal: uploading still works, the progress line just stays blank. */
      });
    try {
      const all = await listHierarchyImports(projectId);
      // remaining === 0 is dropped: the backend prunes spent records on every
      // delete, but a row can also reach zero by a path that does not -- a
      // surveyor deleting shapes one at a time -- and "0 of 3104 still present"
      // reads as "this file is still imported" when nothing of it remains.
      const mine = all.filter((i) => i.level === FEATURE_LEVEL && i.remaining > 0);
      setImports(mine);
      return mine;
    } catch {
      // Empty rather than null on failure, so the list shows its "nothing
      // recorded" state instead of a spinner that never resolves.
      setImports([]);
      return [];
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload, refreshKey]);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      const file = files?.[0];
      if (!file) return;
      if (!/\.(geo)?json$/i.test(file.name)) {
        setError(t.propertyImportPanel.onlyGeojsonAccepted);
        return;
      }
      setError(null);
      setResult(null);
      setUploading(file.name);
      // Ids present before the upload, so the new record can be told apart from
      // them. The import response carries counts but not its own id, and asking
      // the server to return one only so the UI can diff a list it is about to
      // refetch anyway would be a worse trade than this.
      const before = new Set((imports ?? []).map((i) => i.id));
      try {
        const res = await importProperties(projectId, file);
        setResult(res);
        const after = await reload();
        const fresh = after.filter((i) => !before.has(i.id)).map((i) => i.id);
        setSessionImportIds((prev) => [...prev, ...fresh]);
        onImported();
      } catch (err) {
        setError(err instanceof Error ? err.message : t.propertyImportPanel.importFailed);
      } finally {
        setUploading(null);
        // Re-picking the same file after a failure fires no change event
        // otherwise, so a retry would look like a dead button.
        if (inputRef.current) inputRef.current.value = "";
      }
    },
    [projectId, reload, onImported, imports]
  );

  async function attemptUndo(force: boolean) {
    if (!undoFlow) return;
    setBusy(true);
    setError(null);
    try {
      const res = await undoHierarchyImport(projectId, undoFlow.record.id, force);
      if (res.blocked) {
        setUndoFlow({ ...undoFlow, stage: "blocked", summary: res.summary });
        return;
      }
      setSessionImportIds((prev) => prev.filter((id) => id !== undoFlow.record.id));
      setUndoFlow(null);
      await reload();
      onImported();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.propertyImportPanel.couldntUndoUpload);
      setUndoFlow(null);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Runs only from the SECOND confirmation, and always with force.
   *
   * The first dialog does not touch the network on purpose. Sending
   * force=false there would delete outright whenever nothing happened to be
   * numbered yet -- one click, one dialog -- and the whole point of this path
   * is that it asks twice regardless of what is at stake. By the time this
   * runs, the operator has been shown the exact counts, has confirmed twice,
   * and has exported.
   */
  async function confirmClear() {
    setBusy(true);
    setError(null);
    try {
      const res = await clearProperties(projectId, true);
      if (res.blocked) {
        // force=true is not refused, so this is unreachable in practice. Left
        // as a visible failure rather than a silent success if that ever
        // changes server-side.
        setError(res.summary.message ?? t.propertyImportPanel.serverRefusedDelete);
      }
      setClearStage(null);
      setSessionImportIds([]);
      await reload();
      onImported();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.propertyImportPanel.couldntDeleteOutlines);
      setClearStage(null);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Downloads the project's shapes, and only then unlocks the delete.
   *
   * Required rather than offered, for the same reason as the hierarchy clear: a
   * house number a surveyor assigned in the field cannot be recreated from
   * anything the office holds. Resets whenever a dialog opens, so the file
   * always matches what is about to be deleted.
   */
  async function exportFirst() {
    setExporting(true);
    setError(null);
    try {
      await downloadShapefile({ projectId });
      setExported(true);
    } catch (err) {
      // Nothing collected yet 404s here. That is not a failure to guard
      // against -- there is no field work to lose -- so the gate opens rather
      // than trapping the operator behind an export that cannot succeed.
      const message = err instanceof Error ? err.message : String(err);
      if (/404|no features|nothing to export/i.test(message)) {
        setExported(true);
      } else {
        setError(message);
      }
    } finally {
      setExporting(false);
    }
  }

  const done = counts?.numbered ?? 0;
  const total = counts?.total ?? 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const exportGate = (verb: string) => (
    <div className="rounded bg-surface-sunken p-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-2xs text-ink-secondary">
          {exported ? t.propertyImportPanel.exportedCanVerb(verb) : t.propertyImportPanel.exportFieldDataFirst}
        </span>
        <Button
          size="sm"
          variant={exported ? "secondary" : "primary"}
          onClick={() => void exportFirst()}
          disabled={exporting}
        >
          {exporting ? <Spinner className="h-3 w-3" /> : null}
          {exporting ? t.common.preparing : exported ? t.common.exportAgain : t.common.exportShapefile}
        </Button>
      </div>
    </div>
  );

  return (
    <Panel
      title={t.propertyImportPanel.panelTitle(total)}
      className="h-full min-h-0"
      bodyClassName="flex flex-col min-h-0"
    >
      <div className="flex-none p-3">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            void handleFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
          }}
          className={clsx(
            // Deliberately shallow. This panel's real content is below it --
            // the progress figure, the uploads and the delete -- and in a
            // narrow column the hint wrapped to four lines, which pushed all of
            // that out of view behind a scrollbar. A drop target does not need
            // to be large to be obvious.
            "cursor-pointer rounded border border-dashed px-3 py-2.5 text-center transition",
            dragActive
              ? "border-accent bg-accent-subtle"
              : "border-line-strong hover:border-ink-muted hover:bg-surface-sunken"
          )}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".geojson,.json"
            className="hidden"
            onChange={(e) => void handleFiles(e.target.files)}
          />
          <p className="text-xs text-ink-secondary">
            {uploading ? t.propertyImportPanel.importingFile(uploading) : t.propertyImportPanel.dropHint}
          </p>
          {!uploading && (
            // One short line. The longer explanation it replaced said the same
            // thing in four wrapped lines, and the fact worth keeping is only
            // that re-uploading is safe.
            <p className="mt-0.5 text-2xs text-ink-muted">
              {t.propertyImportPanel.reuploadHint}
            </p>
          )}
        </div>

        {error && (
          <div className="mt-2">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}

        {/* Outcome of the last import. `duplicates` is shown apart from the
            general skip count on purpose: re-uploading a newer file and having
            every unchanged outline skipped is the update path working, and
            rolling it into "skipped" would read as a file full of problems. */}
        {result && (
          <div className="mt-2 rounded bg-surface-sunken p-2.5 text-2xs">
            <p className="font-medium text-ink">
              {t.propertyImportPanel.createdAdded(result.created)}
              {result.duplicates > 0 && t.propertyImportPanel.alreadyPresent(result.duplicates)}
              {result.unplaced > 0 && t.propertyImportPanel.outsideEveryBlock(result.unplaced)}
            </p>
            {result.created === 0 && result.duplicates > 0 && result.unplaced === 0 && (
              <p className="mt-0.5 text-ink-muted">
                {t.propertyImportPanel.everythingAlreadyImported}
              </p>
            )}
            {result.reprojected && result.source_crs && (
              <p className="mt-0.5 text-ink-muted">{t.propertyImportPanel.reprojectedFrom(result.source_crs)}</p>
            )}
            {result.unplaced > 0 && (
              <p className="mt-0.5 text-ink-muted">
                {t.propertyImportPanel.unplacedHint}
              </p>
            )}
          </div>
        )}

        {/* Numbering is the survey. An outline exists the moment it is uploaded,
            but it is not surveyed until someone stands in front of the building,
            so the progress figure is the numbered count, not the total. */}
        {total > 0 && (
          <div className="mt-2.5">
            <div className="flex items-center justify-between text-2xs">
              <span className="text-ink-secondary">{t.propertyImportPanel.numberedInField}</span>
              <span className="tabular font-medium text-ink">
                {t.propertyImportPanel.numberedOfTotal(done, total)}
              </span>
            </div>
            <div
              className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-sunken"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t.propertyImportPanel.propertiesNumberedLabel}
            >
              <div
                className="h-full rounded-full bg-accent transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="thin-scroll min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {imports === null || counts === null ? (
          <p className="flex items-center gap-2 text-2xs text-ink-muted">
            <Spinner className="h-3 w-3" /> {t.propertyImportPanel.loadingUploads}
          </p>
        ) : total === 0 ? (
          // Genuinely nothing: no outlines exist at all. This is the only
          // case "upload a GeoJSON" is the right instruction.
          <EmptyState
            title={t.propertyImportPanel.noOutlinesYetTitle}
            body={t.propertyImportPanel.noOutlinesYetBody}
          />
        ) : imports.length === 0 ? (
          // total > 0 but no upload record: outlines exist -- numbered above,
          // visible on the map -- they just weren't created by a file this
          // panel tracked (created before this feature shipped, or via the
          // field app directly). Telling the operator to "upload a GeoJSON"
          // here would be actively wrong: there is nothing missing to upload.
          <p className="px-1 py-2 text-2xs text-ink-muted">
            {t.propertyImportPanel.noUploadsRecordedFor(total)}
          </p>
        ) : (
          <>
            <p className="text-2xs font-semibold uppercase tracking-wider text-ink-muted">
              {t.propertyImportPanel.uploadsTitle}
            </p>
            <ul className="mt-1.5 space-y-1">
              {imports.map((imp) => {
                const undoable = sessionImportIds.includes(imp.id);
                return (
                  <li
                    key={imp.id}
                    className="flex items-center justify-between gap-2 rounded bg-surface-raised px-3 py-1.5 text-2xs shadow-ring"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-ink">
                        {imp.filename ?? t.propertyImportPanel.propertyImportFallback}
                      </p>
                      <p className="text-ink-muted" title={t.propertyImportPanel.remainingRowsTitle}>
                        {/* What an undo would remove NOW -- rows can have gone by
                            other paths, so created_count alone would overstate it. */}
                        {imp.remaining} of {imp.created_count}
                        {imp.created_by ? ` · ${imp.created_by}` : ""} ·{" "}
                        {formatDate(imp.created_at, lang, {
                          day: "2-digit",
                          month: "short",
                          year: "2-digit",
                        })}
                      </p>
                    </div>
                    {undoable && (
                      <Button
                        size="sm"
                        variant="danger"
                        className="h-7 shrink-0 px-2 text-2xs"
                        disabled={busy}
                        onClick={() => {
                          setError(null);
                          setExported(false);
                          setUndoFlow({ record: imp, stage: "confirm" });
                        }}
                      >
                        {t.common.undo}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>

            {sessionImportIds.length > 0 && (
              <p className="mt-1.5 text-2xs text-ink-muted">
                {t.propertyImportPanel.undoOfferNote}
              </p>
            )}

          </>
        )}
      </div>

      {/* Pinned, not scrolled with the list.
          This lived at the bottom of the scroll region, which on a panel this
          short meant the whole destructive action was squeezed into a ~40px
          viewport with its own scrollbar -- a target you had to scroll to find
          and then hit precisely. A footer costs the list one row and makes the
          action a full-width control that is always where you left it. */}
      {total > 0 && (
        <div className="flex-none border-t border-line p-2">
          <Button
            variant="danger"
            className="w-full"
            disabled={busy}
            onClick={() => {
              setError(null);
              setExported(false);
              setClearStage("first");
            }}
          >
            {t.propertyImportPanel.deleteAllOutlines}
          </Button>
        </div>
      )}

      {/* --- Undo: the mistake you just made --- */}
      <ConfirmDialog
        open={undoFlow?.stage === "confirm"}
        title={`Undo ${undoFlow?.record.filename ?? "this upload"}?`}
        body={
          <>
            Removes the {undoFlow?.record.remaining ?? 0} property outline(s) this
            upload created, leaving every other upload in place. You would upload
            the file again to get them back.
          </>
        }
        confirmLabel="Undo import"
        confirmDisabled={!exported}
        busy={busy}
        onCancel={() => setUndoFlow(null)}
        onConfirm={() => void attemptUndo(false)}
      >
        {exportGate(t.propertyImportPanel.verbUndo)}
      </ConfirmDialog>

      <ConfirmDialog
        open={undoFlow?.stage === "blocked"}
        title={t.propertyImportPanel.destroysFieldWorkTitle}
        body={
          <>
            <span className="font-medium text-ink">
              {t.propertyImportPanel.destroysFieldWorkBody(undoFlow?.summary?.features ?? 0)}
            </span>{" "}
            {t.propertyImportPanel.destroysFieldWorkBodySuffix}
          </>
        }
        confirmLabel={t.propertyImportPanel.deleteAnyway}
        confirmDisabled={!exported}
        busy={busy}
        onCancel={() => setUndoFlow(null)}
        onConfirm={() => void attemptUndo(true)}
      >
        {exportGate(t.propertyImportPanel.verbDelete)}
      </ConfirmDialog>

      {/* --- Delete everything: two confirmations, always --- */}
      <ConfirmDialog
        open={clearStage === "first"}
        title={t.propertyImportPanel.deleteAllQuestion(total)}
        body={
          <>
            {t.propertyImportPanel.deleteAllBodyPrefix}{" "}
            <span className="font-medium text-ink">{done}</span>{" "}
            {t.propertyImportPanel.deleteAllBodySuffix}
          </>
        }
        confirmLabel={t.propertyImportPanel.continueLabel}
        confirmDisabled={!exported}
        busy={busy}
        onCancel={() => setClearStage(null)}
        onConfirm={() => setClearStage("final")}
      >
        {exportGate(t.propertyImportPanel.verbContinue)}
      </ConfirmDialog>

      {/* Second confirmation, always. Names what is actually lost rather than
          repeating the first dialog: the outlines come back by re-uploading the
          file, the numbering does not. */}
      <ConfirmDialog
        open={clearStage === "final"}
        title={t.propertyImportPanel.lastChanceTitle}
        body={
          <>
            Permanently deleting{" "}
            <span className="font-medium text-ink">{t.propertyImportPanel.outlineCount(total)}</span>
            {done > 0 ? (
              <>
                {t.propertyImportPanel.ofWhich}{" "}
                <span className="font-medium text-ink">{done}</span>{" "}
                {t.propertyImportPanel.carryHouseNumber}
              </>
            ) : (
              <>{t.propertyImportPanel.noneNumberedYet}</>
            )}
          </>
        }
        confirmLabel={t.propertyImportPanel.deletePermanently}
        confirmDisabled={!exported}
        busy={busy}
        onCancel={() => setClearStage(null)}
        onConfirm={() => void confirmClear()}
      >
        {exportGate(t.propertyImportPanel.verbDelete)}
      </ConfirmDialog>
    </Panel>
  );
}
